import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface DrawingFile {
  id: string;
  file: File;
  name: string;
  category: string;
  revision: string;
  status: string;
}

interface ProductDataFile {
  id: string;
  file: File;
  name: string;
  productId: number;
  description: string;
}

interface SubmittalData {
  projectId: number | null;
  submittalNumber: string;
  submittalTitle: string;
  submittedToId: number | null; // Contact ID
  submittedById: number | null; // User ID
  submissionDate: Date | null;
  drawings: DrawingFile[];
  productData: ProductDataFile[];
  specifications: File[];
  notes: string;
  priority: string;
  status: string;
  responseRequired: boolean;
  responseDate: Date | null;
}

class SubmittalPackageWizardStore {
  rootStore: RootStore;

  // Observables
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  submittalData: SubmittalData = {
    projectId: null,
    submittalNumber: "",
    submittalTitle: "",
    submittedToId: null,
    submittedById: null,
    submissionDate: new Date(),
    drawings: [],
    productData: [],
    specifications: [],
    notes: "",
    priority: "Medium",
    status: "Draft",
    responseRequired: true,
    responseDate: null,
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.generateSubmittalNumber();
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    this.activeStep++;
  };

  prevStep = () => {
    this.activeStep--;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateSubmittalData = <K extends keyof SubmittalData>(
    field: K,
    value: SubmittalData[K]
  ) => {
    this.submittalData[field] = value;
  };

  generateSubmittalNumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    this.submittalData.submittalNumber = `SUB-${timestamp}`;
  };

  selectProject = (projectId: number) => {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    const project = jobStore?.entities.find((p: any) => p.id === projectId);

    runInAction(() => {
      this.submittalData.projectId = projectId;
      this.submittalData.submittalTitle = `${project?.name || "Project"} Submittal`;
    });
  };

  addDrawings = (files: FileList) => {
    const newDrawings: DrawingFile[] = Array.from(files).map(file => ({
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      category: "Architectural",
      revision: "A",
      status: "For Review",
    }));

    runInAction(() => {
      this.submittalData.drawings = [...this.submittalData.drawings, ...newDrawings];
    });
  };

  removeDrawing = (id: string) => {
    this.submittalData.drawings = this.submittalData.drawings.filter(d => d.id !== id);
  };

  updateDrawing = (id: string, field: keyof DrawingFile, value: any) => {
    const drawing = this.submittalData.drawings.find(d => d.id === id);
    if (drawing && field !== 'id' && field !== 'file') {
      (drawing as any)[field] = value;
    }
  };

  addProductData = (files: FileList) => {
    const newProductData: ProductDataFile[] = Array.from(files).map(file => ({
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      productId: 0,
      description: "",
    }));

    runInAction(() => {
      this.submittalData.productData = [...this.submittalData.productData, ...newProductData];
    });
  };

  removeProductData = (id: string) => {
    this.submittalData.productData = this.submittalData.productData.filter(p => p.id !== id);
  };

  updateProductData = (id: string, field: keyof ProductDataFile, value: any) => {
    const productData = this.submittalData.productData.find(p => p.id === id);
    if (productData && field !== 'id' && field !== 'file') {
      (productData as any)[field] = value;
    }
  };

  addSpecifications = (files: FileList) => {
    const newSpecs = Array.from(files);
    runInAction(() => {
      this.submittalData.specifications = [...this.submittalData.specifications, ...newSpecs];
    });
  };

  removeSpecification = (index: number) => {
    this.submittalData.specifications = this.submittalData.specifications.filter((_, i) => i !== index);
  };

  // Async actions
  createSubmittal = async () => {
    this.loading = true;
    this.error = null;

    try {
      const submittalsStore = this.rootStore.submittalsStore as ServiceStore;

      const submittalData = {
        projectId: this.submittalData.projectId,
        submittalNumber: this.submittalData.submittalNumber,
        title: this.submittalData.submittalTitle,
        submittedToId: this.submittalData.submittedToId,
        submittedById: this.submittalData.submittedById,
        submissionDate: this.submittalData.submissionDate,
        drawingCount: this.submittalData.drawings.length,
        productDataCount: this.submittalData.productData.length,
        specificationCount: this.submittalData.specifications.length,
        notes: this.submittalData.notes,
        priority: this.submittalData.priority,
        status: "Submitted",
        responseRequired: this.submittalData.responseRequired,
        responseDate: this.submittalData.responseDate,
      };

      const newSubmittal = await submittalsStore?.create(submittalData);

      runInAction(() => {
        this.success = "Submittal package created successfully!";
        this.nextStep();
      });

      return newSubmittal;
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to create submittal package";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.submittalData = {
      projectId: null,
      submittalNumber: "",
      submittalTitle: "",
      submittedToId: null,
      submittedById: null,
      submissionDate: new Date(),
      drawings: [],
      productData: [],
      specifications: [],
      notes: "",
      priority: "Medium",
      status: "Draft",
      responseRequired: true,
      responseDate: null,
    };
    this.generateSubmittalNumber();
  };

  // Computed values
  get canProceedFromStep1() {
    return !!this.submittalData.projectId && !!this.submittalData.submittalTitle;
  }

  get canProceedFromStep2() {
    return this.submittalData.drawings.length > 0 || this.submittalData.productData.length > 0;
  }

  get canProceedFromStep3() {
    return !!this.submittalData.submittedToId;
  }

  get canCreateSubmittal() {
    return this.canProceedFromStep1 && this.canProceedFromStep2 && this.canProceedFromStep3;
  }

  get projects() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return (jobStore?.entities || []).filter((p: any) => p.stage !== "Completed");
  }

  get contacts() {
    const contactsStore = this.rootStore.contactsStore as ServiceStore;
    return contactsStore?.entities || [];
  }

  get users() {
    const userStore = this.rootStore.userStore as ServiceStore;
    return userStore?.entities || [];
  }

  get products() {
    const productsStore = this.rootStore.productsStore as ServiceStore;
    return productsStore?.entities || [];
  }

  get engineers() {
    return this.users.filter((u: any) => u.role === "Engineer");
  }

  get selectedProject() {
    if (!this.submittalData.projectId) return null;
    return this.projects.find((p: any) => p.id === this.submittalData.projectId);
  }

  get selectedContact() {
    if (!this.submittalData.submittedToId) return null;
    return this.contacts.find((c: any) => c.id === this.submittalData.submittedToId);
  }

  get selectedSubmitter() {
    if (!this.submittalData.submittedById) return null;
    return this.users.find((u: any) => u.id === this.submittalData.submittedById);
  }

  get totalFileCount() {
    return this.submittalData.drawings.length +
           this.submittalData.productData.length +
           this.submittalData.specifications.length;
  }

  get drawingCategories() {
    return [
      "Architectural",
      "Structural",
      "MEP",
      "Site Plan",
      "Details",
      "Sections",
      "Elevations",
    ];
  }

  get drawingStatuses() {
    return [
      "For Review",
      "For Approval",
      "Approved",
      "Revise & Resubmit",
      "Rejected",
    ];
  }

  get priorities() {
    return ["Low", "Medium", "High", "Critical"];
  }
}

export default SubmittalPackageWizardStore;